import { lowerFirst } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withPermission } from '../../../hoc/withPermission';
import api from '../../../infrastructure/api/api';
import {
  ACCT_ASSIGN_PERMISSION,
  anyGuid,
  emptyGuid,
  positionTypes,
} from '../../../utils/constants';
import { filterPositions } from '../../../utils/filtering';
import { sortTemplates } from '../../../utils/sorting';
import { LoadingOverlay } from '../../loading-overlay/index';
import { ApplyTemplateToAllPositions } from './ApplyTemplateToAllPositions';
import { extractUniqueCounterparties } from './functions';
import PositionsFiltering from './positionsFiltering';
import PositionsTable from './positionsTable';
import { useStyles } from './styles';

const MassUpdates = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [allPositions, setAllPositions] = useState([]);
  const [positions, setPositions] = useState([]);
  const [counterparties, setCounterparties] = useState([]);
  const [templateOptions, setTemplateOptions] = useState({
    bondOptions: [],
    loanOptions: [],
    swapOptions: [],
    capOptions: [],
    certificateOptions: [],
    creditFacilityOptions: [],
  });
  const [filters, setFilters] = useState({
    positionTemplateId: emptyGuid,
    portfolio: anyGuid,
    positionType: anyGuid,
    counterparty: anyGuid,
  });

  const updatePositions = (positionId, positionTemplateId) => {
    setPositions(previousPositions => {
      const position = previousPositions.find(p => p.id === positionId);
      position.positionTemplateId = positionTemplateId;
      return [...previousPositions];
    });
  };

  const searchPositions = (
    positionTemplateId,
    positionType,
    counterparty,
    portfolio
  ) => {
    const result = filterPositions(
      allPositions,
      positionTemplateId,
      positionType,
      counterparty,
      portfolio
    );
    setPositions(result);
  };

  const handleApplyToAllAction = async positionTemplateId => {
    setIsLoading(true);

    api
      .post(
        'massUpdates/ApplyAll',
        {
          positionIds: positions.map(position => position.id),
          positionTemplateId,
        },
        enqueueSnackbar,
        t
      )
      .then(updatedPositions => {
        updatedPositions.data.forEach(position => {
          updatePositions(position, positionTemplateId);
        });

        if (positions.length !== updatedPositions.data.length) {
          enqueueSnackbar(t('massUpdatesPage.failedToUpdateSomePositions'), {
            variant: 'warning',
            persist: true,
          });
        }

        return api.get(
          `positionTemplates/getAll?positionType=${filters.positionType}`
        );
      })
      .then(results => {
        setTemplateOptions(prevTemplateOptions => {
          prevTemplateOptions[`${lowerFirst(filters.positionType)}Options`] =
            sortTemplates(results);
          return { ...prevTemplateOptions };
        });
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    api.get('positions').then(positionsResult => {
      if (positionsResult) {
        setAllPositions(positionsResult);
        setCounterparties(extractUniqueCounterparties(positionsResult));
        setPositions(filterPositions(positionsResult));
      }
    });

    Promise.all(
      positionTypes.map(pt =>
        api.get(
          `positionTemplates/getAll?positionType=${pt}`,
          enqueueSnackbar,
          t
        )
      )
    ).then(results => {
      const tOptions = {
        bondOptions: [],
        loanOptions: [],
        swapOptions: [],
        capOptions: [],
        certificateOptions: [],
        creditFacilityOptions: [],
      };

      positionTypes.forEach((pt, index) => {
        tOptions[`${lowerFirst(pt)}Options`] = sortTemplates(results[index]);
      });

      setTemplateOptions(tOptions);
    });
  }, [enqueueSnackbar, t, setCounterparties, setAllPositions, setPositions]);

  return (
    <div className={classes.Container}>
      {isLoading && <LoadingOverlay />}

      <PositionsFiltering
        templateOptions={templateOptions}
        searchPositions={searchPositions}
        counterparties={counterparties}
        setFilters={setFilters}
      />
      <ApplyTemplateToAllPositions
        templateOptions={templateOptions}
        filters={filters}
        handleApplyToAllAction={handleApplyToAllAction}
      />
      <PositionsTable
        templateOptions={templateOptions}
        tableRows={positions}
        updatePositions={updatePositions}
      />
    </div>
  );
};

export default withPermission([ACCT_ASSIGN_PERMISSION])(MassUpdates);
